import _ChevronRight from "@mui/icons-material/ChevronRight";
import _Divider from "@mui/material/Divider";
import _Typography from "@mui/material/Typography";
import _Box from "@mui/material/Box";
import _Skeleton from "@mui/material/Skeleton";
import _CircularProgress from "@mui/material/CircularProgress";
import _TextField from "@mui/material/TextField";
import _InputAdornment from "@mui/material/InputAdornment";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Button2 from "@mui/material/Button";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import emptyStateIllustration from '../../../assets/images/find-illus.svg';
import { useChallenge } from '../../../hooks';
import useEventIndividualRankingQuery from '../../../hooks/use-event-individual-ranking-query';
import PodiumCard, { podiumOrder, podiumRanksConfig } from '../../event/leaderboard/podium-card';
import { ExportIcon, SearchIcon } from '../../icons';
import { UserAvatar } from '../../user';
import LeaderboardIndividualItem from './item';
import RankCard from './rank-card';
import { useIndividualLeaderboardDownload } from './use-download';
function IndividualLeaderboardHome({
  Button: _Button,
  isFree,
  isMember,
  event,
  freeOfferEmployeesLeaderboardsMaxRowsCount,
  currentUser,
  displayLeaderboard,
  onExportReady,
  setSearch
}) {
  const [tmpSearch, setTmpSearch] = useState('');
  const {
    t
  } = useTranslation();
  const {
    downloading,
    handleDownload
  } = useIndividualLeaderboardDownload();
  const {
    getProgressValueWithUnit
  } = useChallenge();
  const {
    data: topResult
  } = useEventIndividualRankingQuery(isMember ? event.id : undefined, {
    page: 1,
    pageSize: isFree ? freeOfferEmployeesLeaderboardsMaxRowsCount : 10
  });
  const leaderboard = isMember === false ? [] : topResult?.items;
  const numberOfParticipants = isMember === false ? 0 : topResult?.count;
  const {
    data: userResult
  } = useEventIndividualRankingQuery(isMember && currentUser ? event.id : undefined, {
    page: 1,
    pageSize: 1,
    getConnectedUserRank: true
  });
  const userItem = isMember === false ? null : currentUser === undefined ? undefined : currentUser === null ? null : userResult?.items[0] ?? null;
  useEffect(() => {
    if (!onExportReady) return;
    onExportReady(numberOfParticipants ? () => handleDownload(event, numberOfParticipants) : undefined);
    return () => onExportReady(undefined);
  }, [numberOfParticipants]);
  const Button = _Button || _Button2;
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#fff",
    borderRadius: 4,
    display: "flex",
    gap: 1,
    padding: 3
  }, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    margin: "none",
    onChange: ({
      currentTarget: {
        value
      }
    }) => setTmpSearch(value),
    onKeyDown: ({
      key
    }) => {
      if (key === 'Enter') {
        setSearch(tmpSearch);
        if (tmpSearch) displayLeaderboard(true);
      }
    },
    placeholder: t('commons.challenges.leaderboard.search_cyclist') || '',
    size: "small",
    slotProps: {
      input: {
        endAdornment: tmpSearch && /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "end"
        }, /*#__PURE__*/React.createElement(_Tooltip, {
          title: "Rechercher"
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_IconButton, {
          disabled: !tmpSearch,
          onClick: () => {
            setSearch(tmpSearch);
            if (tmpSearch) displayLeaderboard(true);
          },
          size: "small"
        }, /*#__PURE__*/React.createElement(SearchIcon, {
          fontSize: "small"
        })))))
      }
    },
    sx: {
      '.MuiInputBase-root': {
        bgcolor: '#fff'
      }
    },
    type: "text",
    value: tmpSearch,
    variant: "outlined"
  }), !onExportReady && /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0
  }, numberOfParticipants !== undefined ? numberOfParticipants > 0 ? /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: downloading,
    onClick: () => numberOfParticipants && handleDownload(event, numberOfParticipants),
    startIcon: downloading ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(ExportIcon, null),
    variant: "text"
  }, t('commons.actions.export')) : /*#__PURE__*/React.createElement(React.Fragment, null) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 36,
    variant: "rounded",
    width: 100
  }))), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#fff",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingBottom: 3,
    paddingTop: 2,
    paddingX: 3
  }, !leaderboard || leaderboard.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    sx: {
      containerType: 'inline-size'
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-end",
    display: {
      '@': 'none',
      '@600': 'flex'
    },
    gap: 3,
    width: "100%"
  }, podiumOrder.map(({
    itemIndex,
    rank
  }) => {
    const item = leaderboard?.[itemIndex];
    const podiumLoading = !leaderboard;
    return /*#__PURE__*/React.createElement(PodiumCard, {
      avatar: podiumLoading || item ? /*#__PURE__*/React.createElement(UserAvatar, {
        size: podiumRanksConfig[rank].avatarSize,
        user: item?.user
      }) : null,
      key: rank,
      rank: rank
    }, /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      fontSize: "0.9375rem",
      fontWeight: 600,
      title: item?.user.username
    }, item ? item.user.username : podiumLoading ? /*#__PURE__*/React.createElement(_Skeleton, {
      variant: "text",
      width: "100%"
    }) : '–'), /*#__PURE__*/React.createElement(_Typography, {
      fontSize: "1.125rem",
      fontWeight: 800
    }, item ? item.progressValue === 0 ? '-' : getProgressValueWithUnit(event.challengeTemplate?.type || 'bttf', item.progressValue) : podiumLoading ? /*#__PURE__*/React.createElement(_Skeleton, {
      variant: "text",
      width: 100
    }) : '–'));
  })), /*#__PURE__*/React.createElement(RankCard, {
    challengeTemplate: event.challengeTemplate,
    numberOfParticipants: numberOfParticipants,
    progress: userItem
  }), /*#__PURE__*/React.createElement(_Box, {
    border: "1px solid #dde0e7",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    width: "100%"
  }, leaderboard ? leaderboard.map((item, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: item.user.id
  }, index > 0 && /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(LeaderboardIndividualItem, {
    challengeType: event.challengeTemplate?.type,
    item: item,
    prevItemRank: index > 0 ? leaderboard[index - 1].rank : null
  }))) : new Array(10).fill(null).map((_, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: index
  }, index > 0 && /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(LeaderboardIndividualItem, {
    challengeType: event.challengeTemplate?.type,
    prevItemRank: null
  }))))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    endIcon: /*#__PURE__*/React.createElement(_ChevronRight, null),
    onClick: () => displayLeaderboard(true),
    size: "medium",
    variant: "contained"
  }, t('commons.challenges.leaderboard.actions.see_entire_leaderboard')))) : /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingY: 5
  }, /*#__PURE__*/React.createElement("img", {
    height: 64,
    src: emptyStateIllustration
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700
  }, t(isMember === false ? 'commons.challenges.leaderboard.empty_state_title_not_member' : 'commons.challenges.leaderboard.empty_state_title_individual')), /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    variant: "body2"
  }, t(isMember === false ? 'commons.challenges.leaderboard.empty_state_description_not_member' : 'commons.challenges.leaderboard.empty_state_description'))))));
}
export default IndividualLeaderboardHome;